import React, {useState, createContext, ReactNode, useEffect} from 'react'
import {IntlProvider} from 'react-intl'

import messagesEn from '../../assets/data/locales/en.json'
import messagesTr from '../../assets/data/locales/tr.json'
import messagesRu from '../../assets/data/locales/ru.json'
import messagesIt from '../../assets/data/locales/it.json'
import messagesAr from '../../assets/data/locales/ar.json'
import messagesDe from '../../assets/data/locales/de.json'

export const SUPPORTED_LOCALES = ["tr", "en", "ru", "ar", "de", "it"] as const;
type Locale = (typeof SUPPORTED_LOCALES)[number];

export const isSupportedLocale = (l?: string): l is Locale =>
    !!l && SUPPORTED_LOCALES.includes(l as Locale);

export const stripLeadingSlash = (p: string) => p.replace(/^\/+/, "");
export const withLang = (p: string, lang: string) => {
    const l = (lang || "").replace(/^\/+|\/+$/g, ""); // "tr" gibi
    const path = (p || "").trim();

    if (path === "" || path === "/") return `/${l}`;

    return `/${l}${path.startsWith("/") ? path : `/${path}`}`;
};


export const replaceLangInPath = (pathname: string, newLang: string) =>
    pathname.replace(/^\/([^/]+)(?=\/|$)/, `/${newLang}`);

const menuMessages: { [key: string]: { [key: string]: string } } = {
    en: {...messagesEn},
    tr: {...messagesTr},
    ru: {...messagesRu},
    it: {...messagesIt},
    ar: {...messagesAr},
    de: {...messagesDe}
}

interface IntlContextProps {
    locale: string
    switchLanguage: (lang: string) => void
}

const IntlContext = createContext<IntlContextProps | undefined>(undefined)

interface IntlProviderWrapperProps {
    children: ReactNode
}

const IntlProviderWrapper: React.FC<IntlProviderWrapperProps> = ({children}) => {
    const location = window.location;
    const defaultLang = 'tr'
    const storedLang = localStorage.getItem('appLang') || defaultLang

    const [locale, setLocale] = useState<string>(storedLang)
    const [messages, setMessages] = useState<{ [key: string]: string }>(menuMessages[storedLang])

    const switchLanguage = (lang: string) => {
        setLocale(lang)
        setMessages(menuMessages[lang])
        localStorage.setItem('appLang', lang);
        const nextPathname = replaceLangInPath(location.pathname, lang);
        if (nextPathname === location.pathname) return;
        // Query string (ör. ?param=...) ve hash korunur.
        location.assign(nextPathname + location.search + location.hash);
    }

    useEffect(() => {
        if (locale !== storedLang) {
            switchLanguage(storedLang)
        }
    }, [])

    return (
        <IntlContext.Provider value={{locale, switchLanguage}}>
            <IntlProvider key={locale} locale={locale} messages={messages} defaultLocale="tr">
                {children}
            </IntlProvider>
        </IntlContext.Provider>
    )
}

export {IntlProviderWrapper, IntlContext}
